import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Phone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = 'phone_onboarding_dismissed_session';

/**
 * Modal that prompts users with an empty phone number (typically Google-OAuth
 * signups) to complete their profile. Skippable, but re-prompts every session
 * until the phone is saved.
 */
export const PhoneOnboardingModal = () => {
  const { user, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    const dismissed = sessionStorage.getItem(DISMISS_KEY) === '1';
    if (user.needs_phone && !dismissed) {
      setOpen(true);
    } else {
      setOpen(false);
    }
    setName((user.name || '').trim());
  }, [user]);

  const handleSave = async () => {
    const trimmed = phone.trim();
    // Simple client-side validation — server is the source of truth
    if (trimmed.replace(/[^0-9]/g, '').length < 6) {
      toast.error('Please enter a valid phone number');
      return;
    }
    setSaving(true);
    try {
      const payload = { phone: trimmed };
      if (!user.name && name.trim()) payload.name = name.trim();
      await axios.post(`${API}/auth/complete-profile`, payload);
      await refreshUser();
      toast.success('Profile updated');
      sessionStorage.removeItem(DISMISS_KEY);
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save phone number');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setOpen(false);
  };

  if (!user || !user.needs_phone) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleSkip(); }}>
      <DialogContent
        className="max-w-md bg-[#2E3944] text-white border-white/10"
        data-testid="phone-onboarding-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-[var(--brand)]" />
            Complete your profile
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            We need your phone number so clients and teammates can reach you.
            You can skip for now, but we'll ask again next time you sign in.
          </DialogDescription>
        </DialogHeader>

        {!user.name && (
          <div>
            <Label htmlFor="onb-name" className="text-sm text-gray-300">Your name</Label>
            <Input
              id="onb-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              className="mt-1 bg-[#212A31] border-white/10 text-white"
              data-testid="phone-onboarding-name-input"
            />
          </div>
        )}

        <div>
          <Label htmlFor="onb-phone" className="text-sm text-gray-300">
            Phone number <span className="text-red-400">*</span>
          </Label>
          <Input
            id="onb-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+91 98765 43210"
            type="tel"
            className="mt-1 bg-[#212A31] border-white/10 text-white"
            data-testid="phone-onboarding-phone-input"
            autoFocus
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="ghost"
            onClick={handleSkip}
            disabled={saving}
            className="text-gray-400 hover:text-white hover:bg-white/5"
            data-testid="phone-onboarding-skip-btn"
          >
            Skip for now
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !phone.trim()}
            className="bg-[var(--brand)] hover:bg-[var(--brand)]/90 text-white"
            data-testid="phone-onboarding-save-btn"
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PhoneOnboardingModal;
